import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, Sparkles, Target, Clock, Lightbulb, ListChecks, AlertTriangle, Plus, Check,
  Database, BookOpen, Compass, Brain,
} from 'lucide-react';
import { loggedMutation, apiClient } from './loggedFetch';
import logger from '../../lib/logger';
import AIDisclaimerBanner from './AIDisclaimerBanner';
import './StrategyBriefModal.css';

// Source tag rendering
// Tags: [DATA], [PORTFOLIO], [POSITIONING], [INFERENCE]
const SOURCE_TAGS = {
  DATA: { icon: Database, label: 'Contract data', className: 'sbm-tag--data' },
  PORTFOLIO: { icon: BookOpen, label: 'Vendor portfolio', className: 'sbm-tag--portfolio' },
  POSITIONING: { icon: Compass, label: 'Curated positioning', className: 'sbm-tag--positioning' },
  INFERENCE: { icon: Brain, label: 'AI inference', className: 'sbm-tag--inference' },
};

// Parse text with source tags and render with appropriate styling
function parseSourceTags(text) {
  if (!text) return text;

  // Match [TAG] at the start of text or after spaces
  const tagRegex = /\[([A-Z]+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = tagRegex.exec(text)) !== null) {
    // Add text before the tag
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }

    const tagName = match[1];
    const tagConfig = SOURCE_TAGS[tagName];
    if (tagConfig) {
      parts.push({ type: 'tag', tagName, config: tagConfig });
    } else {
      // Unknown tag, render as text
      parts.push({ type: 'text', content: match[0] });
    }
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return parts;
}

function SourceTaggedText({ text }) {
  const parts = parseSourceTags(text);
  if (!Array.isArray(parts)) return <span>{text}</span>;

  // Check if first part is an INFERENCE tag to apply italic styling
  const isInference = parts.length > 0 &&
    parts[0].type === 'tag' &&
    parts[0].tagName === 'INFERENCE';

  return (
    <span className={isInference ? 'sbm-inference-text' : ''}>
      {parts.map((part, i) => {
        if (part.type === 'tag') {
          const Icon = part.config.icon;
          return (
            <span
              key={i}
              className={`sbm-source-tag ${part.config.className}`}
              title={part.config.label}
            >
              <Icon size={10} />
            </span>
          );
        }
        return <span key={i}>{part.content}</span>;
      })}
    </span>
  );
}

/*
 * StrategyBriefModal — opens when the user clicks "Why?" on a competitor
 * row in RenewalCalendar (or DisplacementTable later). Calls the new
 * POST /api/vendor-analysis/:vendor/strategy-brief endpoint, which
 * runs Claude Sonnet 4.6 with the target vendor's portfolio + the
 * specific account context to produce a focused sales-strategy brief.
 *
 * Why an LLM call rather than the static COMPETITIVE_STRATEGY library:
 * the static library is keyed by (target, competitor) pairs that we
 * have to hand-curate. Lots of pairs aren't covered (e.g. Pure Storage
 * vs VMware — they're complementary, not competitive, so the play is
 * account-expansion not displacement). The LLM can reason about that
 * dynamically given the specific PO context.
 *
 * Backend caches per (vendor, competitor, product, agency, play_type)
 * so re-clicks are free.
 */

function formatCurrency(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function StrategyBriefModal({ vendor, opportunity, isOpen, onClose, onAddToPlan }) {
  const queryClient = useQueryClient();
  const [addedToPlan, setAddedToPlan] = React.useState(false);

  // Trigger the POST on open. useMutation rather than useQuery because
  // the request is a mutation by HTTP method (and we want explicit
  // re-fetch behavior on retry).
  const briefMutation = useMutation({
    mutationFn: loggedMutation('StrategyBriefModal:fetch', (body) =>
      apiClient.post(`/api/vendor-analysis/${encodeURIComponent(vendor)}/strategy-brief`, body)
    ),
  });

  React.useEffect(() => {
    if (isOpen && opportunity && !briefMutation.data && !briefMutation.isPending) {
      briefMutation.mutate({
        competitor: opportunity.competitor,
        competitor_product: opportunity.competitor_product || opportunity.top_component || null,
        agency: opportunity.agency,
        contract_value: opportunity.contract_value || opportunity.competitor_spend || null,
        end_date: opportunity.end_date || opportunity.latest_end_date || null,
        play_type: opportunity.play_type || 'offensive',
        sample_description: opportunity.sample_description || null,
        line_count: opportunity.line_count || opportunity.po_line_count || null,
      });
    }
  // eslint-disable-next-line
  }, [isOpen, opportunity?.competitor, opportunity?.agency, opportunity?.competitor_product]);

  if (!isOpen || !opportunity) return null;

  const { data, isPending, error } = briefMutation;

  const handleAddToPlan = () => {
    if (!data || !onAddToPlan) return;
    onAddToPlan({
      ...opportunity,
      ai_brief: data,
      // Compose a richer rationale string that the kanban card can store
      // verbatim so the AI reasoning persists with the play.
      rationale_override: [
        data.summary,
        data.why_now?.length ? `Why now: ${data.why_now.join(' | ')}` : '',
        data.key_angles?.length ? `Angles: ${data.key_angles.join(' | ')}` : '',
        data.proactive_steps?.length ? `Steps: ${data.proactive_steps.join(' | ')}` : '',
      ].filter(Boolean).join('\n\n'),
    });
    setAddedToPlan(true);
    logger.vendorAnalysis.info('StrategyBriefModal:add-to-plan', {
      vendor, competitor: opportunity.competitor, agency: opportunity.agency,
    });
  };

  return (
    <div className="sbm-overlay" onClick={onClose}>
      <div className="sbm-content" onClick={(e) => e.stopPropagation()}>
        <header className="sbm-header">
          <div className="sbm-title">
            <Sparkles size={18} />
            <div>
              <div className="sbm-title-main">
                {vendor} <span className="sbm-vs">vs</span> {opportunity.competitor}
                {opportunity.competitor_product && (
                  <span className="sbm-product"> · {opportunity.competitor_product}</span>
                )}
              </div>
              <div className="sbm-subline">
                {opportunity.agency} ·
                {' '}{formatCurrency(opportunity.contract_value || opportunity.competitor_spend)}
                {opportunity.end_date || opportunity.latest_end_date
                  ? ` · ends ${formatDate(opportunity.end_date || opportunity.latest_end_date)}`
                  : ''}
                {' '}· <span className="sbm-play">{playLabel(opportunity.play_type)}</span>
              </div>
            </div>
          </div>
          <button className="sbm-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="sbm-body">
          {isPending && (
            <div className="sbm-loading">
              <Sparkles size={20} />
              <div>
                <strong>Generating strategy brief…</strong>
                <p>Claude Sonnet is analyzing the {vendor} portfolio against this {opportunity.competitor} renewal in the context of CA state procurement.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="sbm-error">
              <AlertTriangle size={18} />
              <div>
                <strong>Brief failed: {error.message}</strong>
                <p>You can retry — repeated requests for the same opportunity hit a 24h cache, so this won't double-bill.</p>
                <button className="vca-add-btn" onClick={() => briefMutation.reset()} style={{ marginTop: 8 }}>
                  Retry
                </button>
              </div>
            </div>
          )}

          {data && (
            <>
              <AIDisclaimerBanner />

              {data.cached && (
                <div className="sbm-cached">
                  Cached brief (24h TTL). Click Retry below to regenerate.
                </div>
              )}

              {data.validation_warning && (
                <div className="sbm-validation-warning">
                  <AlertTriangle size={14} />
                  <div>
                    <strong>Response required {data.retry_count || 0} retry attempt{data.retry_count !== 1 ? 's' : ''}</strong>
                    <p>Some sections may be incomplete. Review before acting on this brief.</p>
                  </div>
                </div>
              )}

              <Section icon={<Sparkles size={14} />} label="Summary">
                <p className="sbm-summary"><SourceTaggedText text={data.summary} /></p>
              </Section>

              <Section icon={<Clock size={14} />} label="Why now">
                <ul>
                  {(data.why_now || []).map((line, i) => (
                    <li key={i}><SourceTaggedText text={line} /></li>
                  ))}
                </ul>
              </Section>

              <Section icon={<Target size={14} />} label="Key angles">
                <ul>
                  {(data.key_angles || []).map((line, i) => (
                    <li key={i}><SourceTaggedText text={line} /></li>
                  ))}
                </ul>
              </Section>

              <Section icon={<ListChecks size={14} />} label="Proactive steps">
                <ol>
                  {(data.proactive_steps || []).map((line, i) => (
                    <li key={i}><SourceTaggedText text={line} /></li>
                  ))}
                </ol>
              </Section>

              {data.risks?.length > 0 && (
                <Section icon={<AlertTriangle size={14} />} label="Risks to validate" tone="warn">
                  <ul>
                    {data.risks.map((line, i) => (
                      <li key={i}><SourceTaggedText text={line} /></li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}
        </div>

        <footer className="sbm-footer">
          <span className="sbm-footer-meta">
            {data ? 'Brief generated by Claude Sonnet — verify before shipping to a customer.' : ''}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {data && !addedToPlan && onAddToPlan && (
              <button
                className="vca-add-btn"
                onClick={handleAddToPlan}
                title="Seed a kanban card with this AI brief as the rationale"
              >
                <Plus size={14} /> Add to plan with this brief
              </button>
            )}
            {addedToPlan && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-success)' }}>
                <Check size={14} /> Added to plan
              </span>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function Section({ icon, label, tone, children }) {
  return (
    <section className={`sbm-section ${tone === 'warn' ? 'sbm-section--warn' : ''}`}>
      <div className="sbm-section-head">
        {icon}
        <span>{label}</span>
      </div>
      <div className="sbm-section-body">{children}</div>
    </section>
  );
}

function playLabel(play) {
  switch (play) {
    case 'offensive': return 'Offensive · target absent';
    case 'coexist': return 'Coexist · wrap into EA';
    case 'defend': return 'Defend / expand';
    default: return play || '—';
  }
}

export default StrategyBriefModal;
